import React, { useState, useEffect } from 'react';
import { RotateCcw } from 'lucide-react';
import ORIENTATION_CONFIG from '../config/orientation';

/**
 * OrientationLock Component
 * 
 * SADECE MOBİL CİHAZLARDA çalışır.
 * Desktop/geniş ekranlarda hiçbir kontrol yapmaz.
 * 
 * iOS'ta manifest.json orientation desteği sınırlı olduğu için
 * bu component yanlış yönde uyarı mesajı gösterir.
 */
const OrientationLock = ({ children }) => {
  const [isWrongOrientation, setIsWrongOrientation] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  useEffect(() => {
    // Mobil cihaz kontrolü
    const checkIfMobile = () => {
      // 1. User Agent kontrolü
      const mobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      // 2. Touch capability kontrolü
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      // 3. Ekran genişliği kontrolü (768px altı mobil sayılır)
      const isSmallScreen = window.innerWidth <= 768;
      
      // Mobil: User agent mobil VEYA (touch var VE küçük ekran)
      return mobileUserAgent || (hasTouch && isSmallScreen);
    };

    // PWA olarak çalışıp çalışmadığını kontrol et
    const checkPWA = () => {
      return window.matchMedia('(display-mode: standalone)').matches ||
             window.navigator.standalone === true;
    };

    // Orientation değişikliğini dinle
    const checkOrientation = () => {
      const isMobile = checkIfMobile();
      setIsMobileDevice(isMobile);

      // Desktop/geniş ekranda kontrol yapma
      if (!isMobile) {
        setIsWrongOrientation(false);
        return;
      }

      // 'any' modunda kontrol yapma
      if (ORIENTATION_CONFIG.mode === 'any') {
        setIsWrongOrientation(false);
        return;
      }

      const isLandscape = window.innerWidth > window.innerHeight;
      
      if (ORIENTATION_CONFIG.mode === 'portrait' && isLandscape) {
        setIsWrongOrientation(true);
      } else if (ORIENTATION_CONFIG.mode === 'landscape' && !isLandscape) {
        setIsWrongOrientation(true);
      } else {
        setIsWrongOrientation(false);
      }
    };

    // İlk kontrol
    checkOrientation();

    // Event listener'ları ekle
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);

    // Screen Orientation API desteği varsa kullan (Android için)
    if (screen.orientation && ORIENTATION_CONFIG.mode !== 'any' && checkIfMobile()) {
      try {
        // Fullscreen modda orientation kilitlemeyi dene
        if (document.fullscreenElement) {
          screen.orientation.lock(ORIENTATION_CONFIG.mode).catch(() => {
            // Sessizce başarısız ol - iOS'ta desteklenmiyor
          });
        }
      } catch (e) {
        // API desteklenmiyor
      }
    }

    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  // Sadece mobil cihazda ve yanlış orientation'da uyarı göster
  if (isWrongOrientation && isMobileDevice && ORIENTATION_CONFIG.mode !== 'any') {
    const message = ORIENTATION_CONFIG.mode === 'portrait' 
      ? ORIENTATION_CONFIG.messages.portrait 
      : ORIENTATION_CONFIG.messages.landscape;

    return (
      <div className="fixed inset-0 z-[9999] bg-gradient-to-br from-cyan-600 to-blue-700 flex items-center justify-center p-8">
        <div className="text-center text-white max-w-sm">
          {/* Dönen telefon animasyonu */}
          <div className="mb-8 relative">
            <div className={`inline-block ${ORIENTATION_CONFIG.mode === 'portrait' ? 'animate-rotate-to-portrait' : 'animate-rotate-to-landscape'}`}>
              <div className="w-20 h-32 border-4 border-white rounded-2xl relative bg-white/10">
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-8 h-1 bg-white/50 rounded-full"></div>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-6 h-6 border-2 border-white/50 rounded-full"></div>
              </div>
            </div>
            <RotateCcw className="absolute -right-2 top-1/2 -translate-y-1/2 w-8 h-8 text-white/70 animate-spin-slow" />
          </div>

          <h2 className="text-2xl font-bold mb-4">{message.title}</h2>
          <p className="text-white/90 text-lg">{message.description}</p>
          
          {/* OnxFlow Logo */}
          <div className="mt-8 opacity-50">
            <span className="text-sm font-semibold">ONXFLOW</span>
          </div>
        </div>

        <style jsx="true">{`
          @keyframes rotate-to-portrait {
            0%, 100% { transform: rotate(90deg); }
            50% { transform: rotate(0deg); }
          }
          @keyframes rotate-to-landscape {
            0%, 100% { transform: rotate(0deg); }
            50% { transform: rotate(90deg); }
          }
          @keyframes spin-slow {
            from { transform: translateY(-50%) rotate(0deg); }
            to { transform: translateY(-50%) rotate(-360deg); }
          }
          .animate-rotate-to-portrait {
            animation: rotate-to-portrait 3s ease-in-out infinite;
          }
          .animate-rotate-to-landscape {
            animation: rotate-to-landscape 3s ease-in-out infinite;
          }
          .animate-spin-slow {
            animation: spin-slow 3s linear infinite;
          }
        `}</style>
      </div>
    );
  }

  return <>{children}</>;
};

export default OrientationLock;
